import { get, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

import {
	FormSingleLoanDefinition,
	LoanDefinitionFormValues,
} from '@dealstack/frontend/shared/components/LoanDefinitonTable/useLoanDefinitionTableController';

export const useLoanDefinitionRowController = (fieldIndex: number, onRemove: () => void) => {
	const loanDefinitionForm = useFormContext();

	const fieldKeys = useMemo(
		() => ({
			field: `${LoanDefinitionFormValues.loanDefinitions}.${fieldIndex}` as `${LoanDefinitionFormValues.loanDefinitions}.${number}`,
			type: `${LoanDefinitionFormValues.loanDefinitions}.${fieldIndex}.type` as `${LoanDefinitionFormValues.loanDefinitions}.${number}.type`,
			name: `${LoanDefinitionFormValues.loanDefinitions}.${fieldIndex}.name` as `${LoanDefinitionFormValues.loanDefinitions}.${number}.name`,
			funder: `${LoanDefinitionFormValues.loanDefinitions}.${fieldIndex}.funder` as `${LoanDefinitionFormValues.loanDefinitions}.${number}.funder`,
			funderId:
				`${LoanDefinitionFormValues.loanDefinitions}.${fieldIndex}.funderId` as `${LoanDefinitionFormValues.loanDefinitions}.${number}.funderId`,
			funderIsNew:
				`${LoanDefinitionFormValues.loanDefinitions}.${fieldIndex}.funderIsNew` as `${LoanDefinitionFormValues.loanDefinitions}.${number}.funderIsNew`,
			funderName:
				`${LoanDefinitionFormValues.loanDefinitions}.${fieldIndex}.funderName` as `${LoanDefinitionFormValues.loanDefinitions}.${number}.funderName`,
			isOnEdit:
				`${LoanDefinitionFormValues.loanDefinitions}.${fieldIndex}.isOnEdit` as `${LoanDefinitionFormValues.loanDefinitions}.${number}.isOnEdit`,
		}),
		[fieldIndex]
	);

	const fieldData: FormSingleLoanDefinition = loanDefinitionForm.watch(fieldKeys.field);

	const { errors, touchedFields } = useFormState();
	const isValid = !get(errors, fieldKeys.name) && !get(errors, fieldKeys.funderId);

	const [fieldBackupData, setFieldBackupData] = useState<FormSingleLoanDefinition>();

	useEffect(() => {
		if (!fieldData.isOnEdit) {
			return;
		}
		const isFunderTouched =
			get(touchedFields, fieldKeys.funder) || get(touchedFields, fieldKeys.funderId);
		const isLoanNameTouched = get(touchedFields, fieldKeys.name);
		if (isFunderTouched) {
			void loanDefinitionForm.trigger(fieldKeys.funderId);
		}
		if (isLoanNameTouched) {
			void loanDefinitionForm.trigger(fieldKeys.name);
		}
	}, [fieldData.type, fieldData.name, fieldData.funder, fieldData.funderId]);

	const startEdit = () => {
		loanDefinitionForm.setValue(fieldKeys.isOnEdit, true);
	};

	const stopEdit = () => {
		const loanDefinitionFields = get(
			loanDefinitionForm.getValues(),
			LoanDefinitionFormValues.loanDefinitions
		);
		const index = loanDefinitionFields.findIndex((item) => item.id === fieldData.id);
		const field = loanDefinitionFields[index];
		if (!field) {
			return;
		}

		loanDefinitionForm.setValue(fieldKeys.isOnEdit, false);
	};

	const onSaveButtonClicked = () => {
		const isFunderEmpty = !fieldData.funderId || fieldData.funderId.trim().length === 0;
		const isNameEmpty = !fieldData.name || fieldData.name.trim().length === 0;
		if (!isFunderEmpty && !isNameEmpty && isValid) {
			stopEdit();
			return;
		}
		void loanDefinitionForm.trigger();
	};

	const onCancelButtonClicked = () => {
		const isNoBackupName = isEmpty(fieldBackupData?.name?.trim());

		if (((fieldData.name.trim() === '' || !isValid) && isNoBackupName) || isNoBackupName) {
			onRemove();
			return;
		}

		if (!isNoBackupName) {
			loanDefinitionForm.setValue(fieldKeys.field, fieldBackupData);
		}
		stopEdit();
	};

	const onEditButtonClicked = () => {
		setFieldBackupData({ ...fieldData });
		startEdit();
	};

	useEffect(() => {
		if (!fieldData.name && !fieldData.isOnEdit) {
			onEditButtonClicked();
		}
	}, []);

	return {
		fieldKeys,
		fieldData,
		isValid,
		onSaveButtonClicked,
		onCancelButtonClicked,
		onEditButtonClicked,
		stopEdit,
	};
};
